/**
 * sequenceHelpers.js — small shared pieces for the sequence list (SequencesManager) and editor
 * (SequenceUpdater). Server rules live in services/service.emailSequences.js.
 */

// ---- status ----------------------------------------------------------------------
// 'draft' (never turned on) | 'active' | 'paused' (was on, now off)
export const SEQUENCE_STATUS = {
  draft: { label: 'טיוטה', color: '#A1A1AA' },
  active: { label: 'פעיל', color: '#16A34A' },
  paused: { label: 'מושהה', color: '#D97706' },
};
export const statusOf = (sequence) => (sequence && sequence.status) || (sequence && sequence.isActive ? 'active' : 'draft');

// ---- delays: stored as minutes, edited as number + unit ----------------------------
export const UNITS = [
  { value: 1, label: 'דקות' },
  { value: 60, label: 'שעות' },
  { value: 1440, label: 'ימים' },
];

// Largest unit that divides evenly: 4320 → { amount: 3, unit: 1440 }; 90 → { amount: 90, unit: 1 }
export const splitDelay = (minutes) => {
  const m = Number(minutes) || 0;
  if (m === 0) return { amount: 0, unit: 1440 };
  const unit = [1440, 60, 1].find((u) => m % u === 0);
  return { amount: m / unit, unit };
};

export const toMinutes = (amount, unit) => Math.max(0, Math.round((Number(amount) || 0) * unit));

const unitWord = (amount, unit) => {
  if (unit === 1440) return amount === 1 ? 'יום' : 'ימים';
  if (unit === 60) return amount === 1 ? 'שעה' : 'שעות';
  return amount === 1 ? 'דקה' : 'דקות';
};

// "מיד אחרי כניסה לרצף" / "3 ימים אחרי המייל הקודם"
export const delayLabel = (minutes, isFirst) => {
  const anchor = isFirst ? 'אחרי כניסה לרצף' : 'אחרי המייל הקודם';
  if (!minutes) return `מיד ${anchor}`;
  const { amount, unit } = splitDelay(minutes);
  return `${amount} ${unitWord(amount, unit)} ${anchor}`;
};

// Tooltip on the status badge: what the status means right now
export const statusExplanation = (sequence) => {
  const status = statusOf(sequence);
  const waiting = (sequence && sequence.enrollmentCounts && sequence.enrollmentCounts.active) || 0;
  if (status === 'active') return 'לקוחות שמקבלים אחד מהטאגים נכנסים לרצף ומקבלים את המיילים.';
  if (status === 'paused') {
    return waiting
      ? `לא נכנסים לקוחות חדשים ולא נשלחים מיילים. ${waiting} לקוחות מחכים במקום וימשיכו כשתפעילו שוב.`
      : 'לא נכנסים לקוחות חדשים ולא נשלחים מיילים.';
  }
  return 'עוד לא הופעל. אף אחד לא נכנס ולא נשלחים מיילים.';
};

// ---- impact (dry run) → what the admin is told before confirming ------------------------
// Server: POST /api/email/sequences/impact, documented in services/service.emailSequences.js impact().

// Warning codes. blocking = the action can't go ahead.
export const IMPACT_WARNINGS = {
  NAME_REQUIRED: { text: 'נא לתת שם לרצף', blocking: true },
  DUPLICATE_NAME: { text: 'כבר יש רצף בשם הזה', blocking: true },
  TEMPLATE_NOT_FOUND: { text: 'אחת התבניות שנבחרו כבר לא קיימת', blocking: true },
  NO_STEPS: { text: 'אי אפשר להפעיל רצף בלי מיילים', blocking: true },
  ALL_STEPS_REMOVED_ON_ACTIVE: { text: 'אי אפשר להסיר את כל המיילים מרצף פעיל — השהו אותו קודם', blocking: true },
  NO_TRIGGER_TAGS: { text: 'אין טאגי הפעלה — אף אחד לא ייכנס לרצף', blocking: false },
  FIRST_EMAIL_IMMEDIATE: { text: 'המייל הראשון יישלח מיד כשמישהו נכנס לרצף', blocking: false },
};
export const impactWarnings = (impact) =>
  ((impact && impact.warnings) || [])
    .filter((w) => IMPACT_WARNINGS[w.code])
    .map((w) => ({ code: w.code, ...IMPACT_WARNINGS[w.code] }));
export const hasBlockingWarning = (impact) => impactWarnings(impact).some((w) => w.blocking);

const namesOf = (tags) => (tags || []).map((t) => t.name || `#${t.tagId}`).join(', ');

// "לא יקבלו: 3 כבר עברו את הרצף · 1 הוסרו מרשימת התפוצה · …" (only reasons with customers)
export const excludedText = (excluded) => {
  if (!excluded) return '';
  const parts = [
    [excluded.alreadyInSequence, 'כבר נמצאים ברצף או עברו אותו'],
    [excluded.unsubscribed, 'הוסרו מרשימת התפוצה'],
    [excluded.noMarketingConsent, 'לא אישרו דיוור שיווקי'],
    [excluded.noEmail, 'בלי כתובת מייל'],
  ]
    .filter(([n]) => n > 0)
    .map(([n, label]) => `${n} ${label}`);
  return parts.length ? `לא יקבלו: ${parts.join(' · ')}` : '';
};

// The "send also to customers who already have the tag" question is asked when people enter NOW:
// turning on, or saving an active sequence with added tags.
export const asksAboutExisting = (impact) => {
  const holders = impact && impact.existingTagHolders;
  if (!holders || holders.eligible === 0) return false;
  return impact.becomesActive || (impact.isActiveAfter && holders.appliesTo === 'added_tags');
};

/**
 * Lines that explain what the action will do. Only lines with something to say are returned.
 * @returns {Array<{ key, tone: 'info'|'warn'|'danger', text }>}
 */
export const impactLines = (impact) => {
  if (!impact) return [];
  const lines = [];
  const add = (key, tone, text) => lines.push({ key, tone, text });
  const holders = impact.existingTagHolders;

  // who enters
  if (impact.becomesActive && impact.newTagHolders === 'enter_now' && holders) {
    add('enter', 'info', `מעכשיו, כל מי שיקבל את הטאג ${namesOf(holders.tags)} ייכנס לרצף ויתחיל לקבל מיילים.`);
  }
  if ((impact.becomesActive || (impact.isActiveAfter && holders && holders.appliesTo === 'added_tags')) &&
      holders && holders.holders > 0 && holders.eligible === 0) {
    add('noEligible', 'info', `ל-${holders.holders} לקוחות כבר יש את הטאג, אבל אף אחד מהם לא יקבל את הרצף (${excludedText(holders.excluded).replace('לא יקבלו: ', '')}).`);
  }

  // turning on
  const a = impact.activation;
  if (a) {
    if (a.resumeLegacyPaused > 0) {
      add('legacy', 'warn', `${a.resumeLegacyPaused} לקוחות שנעצרו באמצע הרצף הזה (לפני השינוי במערכת) ימשיכו מאיפה שעצרו.`);
    }
    if (a.frozenContinuing > 0) {
      add('frozen', 'info', `${a.frozenContinuing} לקוחות שחיכו בזמן שהרצף היה מושהה ימשיכו בדיוק מאיפה שעצרו.`);
    }
    if (a.overdueSendNow > 0) {
      add('overdue', 'warn', `${a.overdueSendNow} מהם יקבלו מייל מיד, כי המועד שלהם כבר עבר.`);
    }
  }

  // turning off
  const d = impact.deactivation;
  if (d) {
    add('off', 'info', 'אף אחד חדש לא ייכנס, ולא יישלחו מיילים מהרצף עד שתפעילו אותו שוב.');
    if (d.frozen > 0) add('offFrozen', 'info', `${d.frozen} לקוחות שבאמצע הרצף יחכו במקום, וימשיכו מאותה נקודה כשתפעילו שוב.`);
    if (d.dueWithin24h > 0) add('offDue', 'warn', `${d.dueWithin24h} מהם היו אמורים לקבל מייל ב-24 השעות הקרובות.`);
  }

  // removed tags
  const s = impact.stop;
  if (s && s.total > 0) {
    add('stop', 'danger', `${s.total} לקוחות שבאמצע הרצף נכנסו דרך ${namesOf(s.tags)} — הם יפסיקו לקבל את המיילים של הרצף.`);
  }

  // step edits vs customers in the middle
  const st = impact.steps;
  if (st) {
    if (st.movedPastDeletedEmail > 0) add('moved', 'warn', `${st.movedPastDeletedEmail} לקוחות חיכו למייל שנמחק — הם יעברו למייל הבא אחריו.`);
    if (st.completedByDeletion > 0) add('completed', 'warn', `${st.completedByDeletion} לקוחות חיכו למייל שנמחק ואין אחריו מיילים — הם יסיימו את הרצף.`);
    if (st.skipNewEmails > 0) add('skipNew', 'info', `${st.skipNewEmails} לקוחות כבר עברו את המקום של מייל חדש שהוספתם — הם לא יקבלו אותו.`);
    if (st.keepScheduledTime > 0) add('keepTime', 'info', `${st.keepScheduledTime} לקוחות כבר מתוזמנים למייל שההשהייה שלו השתנתה — הם יקבלו אותו בזמן שנקבע קודם.`);
  }

  // רצף שירות
  const t = impact.transactional;
  if (t && t.change === 'to_transactional') {
    add('trans', 'info', 'הרצף יישלח גם למי שלא אישר/ה דיוור שיווקי (לא למי שהוסר/ה מרשימת התפוצה).');
    if (t.resume > 0) add('transResume', 'info', `${t.resume} לקוחות שנעצרו בגלל זה ימשיכו.`);
  }
  if (t && t.change === 'to_marketing') {
    add('mkt', 'info', 'מעכשיו רק מי שאישר/ה דיוור שיווקי יקבל/תקבל את הרצף.');
    if (t.willPauseAtNextSend > 0) add('mktPause', 'warn', `${t.willPauseAtNextSend} לקוחות באמצע הרצף שלא אישרו דיוור ייעצרו לפני המייל הבא שלהם.`);
  }

  // delete
  const del = impact.deletion;
  if (del) {
    if (del.inProgress > 0) add('delProgress', 'danger', `${del.inProgress} לקוחות באמצע הרצף יפסיקו לקבל מיילים.`);
    if (del.sentEmails > 0) {
      add('delSent', 'warn', `נשלחו ${del.sentEmails} מיילים מהרצף. ההיסטוריה שלהם תישאר אצל הלקוחות, אבל הסטטיסטיקה של הרצף תימחק.`);
    }
    if (del.completed > 0 && !del.sentEmails) add('delCompleted', 'info', `${del.completed} לקוחות כבר סיימו את הרצף.`);
  }
  return lines;
};

// Sending window + daily limit, from impact.sendingLimits
export const sendingLimitsText = (limits) => {
  if (!limits) return '';
  const hours = limits.sendingHours ? `רק בין ${String(limits.sendingHours.from).padStart(2, '0')}:00 ל-${limits.sendingHours.to}:00, ו` : '';
  return `מיילים יוצאים ${hours}עד ${limits.effectivePerDay} ביום לכל המערכת (כולל שאר המיילים).`;
};

// ---- server errors → Hebrew ----------------------------------------------------------
// Server sends { error: { code, message } } (English message).
export const sequenceErrorText = (error, fallback = 'שגיאה בשמירת הרצף') => {
  const e = error && error.response && error.response.data && error.response.data.error;
  if (!e) return fallback;
  if (e.code === 'NO_STEPS') return 'אי אפשר להפעיל רצף בלי מיילים — הוסיפו לפחות מייל אחד';
  if (e.code === 'LAST_STEP_IN_ACTIVE_SEQUENCE') return 'ברצף פעיל חייב להישאר לפחות מייל אחד';
  if (/already exists/i.test(e.message || '')) return 'כבר קיים רצף בשם הזה';
  if (/name is required/i.test(e.message || '')) return 'נא למלא שם רצף';
  return e.message || fallback;
};
